import { img } from "../images";
import type { Blog } from "./types";

const DR_VANITHA = "Dr. M. Vanitha";
const VANITHA_BIO =
  "Dr. M. Vanitha is a Dermatologist at AV Clinic, Krishnagiri (M.B.B.S., D.D.V.L.). She provides specialist care for a wide range of skin, hair and nail conditions — from acne and pigmentation to psoriasis, eczema, fungal infections and hair fall — combining accurate diagnosis with practical, personalised treatment plans.";
const VANITHA_ROLE = "Dermatologist at AV Clinic, Krishnagiri";

export const hairBlogs: Blog[] = [
  {
    slug: "hair-loss-causes",
    title: "Common Causes of Hair Loss",
    category: "Hair Care",
    excerpt:
      "Hair loss has many causes. Learn the common reasons and how to identify what is behind your hair fall.",
    image: img.svcHairLoss,
    imageAlt: "Common causes of hair loss",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-28",
    modified: "2026-08-28",
    readTime: "5 min read",
    popular: true,
    tags: ["hair loss", "hair fall", "balding", "dermatology"],
    primaryKeyword: "causes of hair loss",
    secondaryKeywords: ["hair loss causes", "why is my hair falling", "hair fall Krishnagiri"],
    relatedService: "hair-loss",
    relatedServiceLabel: "Hair Loss Treatment",
    relatedArticles: ["hair-fall-men", "hair-fall-women", "hair-loss-treatment"],
    sections: [
      {
        heading: "Some hair shedding is normal",
        body: [
          "It is normal to shed around 50 to 100 hairs a day as part of the natural hair cycle. Hair loss becomes a concern when shedding is noticeably excessive, hair looks thinner, or patches appear.",
        ],
      },
      {
        heading: "Common causes",
        body: ["Hair loss can be caused by:"],
        list: [
          "Genetics and hereditary thinning",
          "Hormonal changes",
          "Stress and illness",
          "Nutritional deficiencies",
          "Certain medical conditions and medications",
        ],
      },
      {
        heading: "Why the cause matters",
        body: [
          "The best treatment depends on the cause. Reversible causes such as stress or deficiency can improve, while hereditary thinning responds best when addressed early.",
        ],
      },
      {
        heading: "What a hair evaluation involves",
        body: [
          "A hair and scalp evaluation examines the pattern of hair loss and your scalp, and considers your history. Based on this, a personalised plan is recommended.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does washing hair cause hair loss?",
        a: "No. Washing removes hairs that were already shed, so it can look like more hair loss than is actually happening.",
      },
      {
        q: "Can hair loss be reversed?",
        a: "Depending on the cause, hair loss can often be slowed or improved, especially with early intervention.",
      },
    ],
    seo: {
      title: "Common Causes of Hair Loss | AV Clinic",
      description:
        "Learn the common causes of hair loss in Krishnagiri and how a hair evaluation identifies the right treatment. Dermatology care at AV Clinic.",
    },
  },
  {
    slug: "hair-fall-men",
    title: "Hair Fall in Men",
    category: "Hair Care",
    excerpt:
      "Hair fall in men is often hereditary but can have other causes. Learn what to expect and how it is managed.",
    image: img.svcHairHealth,
    imageAlt: "Hair fall in men",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-24",
    modified: "2026-08-24",
    readTime: "5 min read",
    tags: ["hair fall men", "male hair loss", "receding hairline", "dermatology"],
    primaryKeyword: "hair fall in men",
    secondaryKeywords: ["male hair loss", "receding hairline", "men hair fall Krishnagiri"],
    relatedService: "hair-loss",
    relatedServiceLabel: "Hair Loss Treatment",
    relatedArticles: ["male-pattern-hair-loss", "hair-loss-causes", "hair-thinning-causes"],
    sections: [
      {
        heading: "Men and hair loss",
        body: [
          "Male hair loss is very common and often follows a hereditary pattern, such as a receding hairline or thinning at the crown. It can start in the late 20s or 30s and is not an illness.",
        ],
      },
      {
        heading: "Possible causes",
        body: ["Male hair loss can be due to:"],
        list: [
          "Hereditary pattern hair loss",
          "Hormonal changes",
          "Stress",
          "Nutritional factors",
          "Underlying health conditions",
        ],
      },
      {
        heading: "What to expect",
        body: [
          "Early intervention gives the best chance of slowing or stabilising hair loss. A specialist can confirm the pattern and advise what is realistic.",
        ],
      },
      {
        heading: "Getting help early",
        body: [
          "If you notice a widening part, a receding hairline or more shedding, a hair evaluation is worthwhile. Acting early is the key to better outcomes.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is male hair loss always hereditary?",
        a: "Not always, but hereditary pattern hair loss is the most common cause in men.",
      },
      {
        q: "Can men's hair loss be treated?",
        a: "Yes, especially when identified early. A dermatologist can advise on options and expectations.",
      },
    ],
    seo: {
      title: "Hair Fall in Men: Causes & Care | AV Clinic",
      description:
        "Learn the causes of hair fall in men and how early treatment can help. Hair loss management at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "hair-fall-women",
    title: "Hair Fall in Women",
    category: "Hair Care",
    excerpt:
      "Hair fall affects women too, and the causes are often different. Learn what drives hair loss in women.",
    image: img.svcHairLoss,
    imageAlt: "Hair fall in women",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-20",
    modified: "2026-08-20",
    readTime: "5 min read",
    tags: ["hair fall women", "female hair loss", "thinning hair", "dermatology"],
    primaryKeyword: "hair fall in women",
    secondaryKeywords: ["female hair loss", "women thinning hair", "hair fall treatment Krishnagiri"],
    relatedService: "hair-loss",
    relatedServiceLabel: "Hair Loss Treatment",
    relatedArticles: ["female-pattern-hair-loss", "hair-thinning-causes", "hair-loss-treatment"],
    sections: [
      {
        heading: "Hair fall is common in women",
        body: [
          "Women experience hair fall for several reasons, and it can be very distressing. Many causes are treatable, especially when addressed early.",
        ],
      },
      {
        heading: "Common causes in women",
        body: ["Hair fall in women is often linked to:"],
        list: [
          "Hormonal changes, including after pregnancy or during menopause",
          "Hereditary thinning",
          "Stress and illness",
          "Nutritional deficiency",
          "Hair-care practices or tight hairstyles",
        ],
      },
      {
        heading: "Post-pregnancy hair fall",
        body: [
          "Many women notice increased shedding a few months after childbirth, which is usually temporary. It typically settles as hormone levels normalise.",
        ],
      },
      {
        heading: "When to see a specialist",
        body: [
          "If hair fall is heavy, persistent, or comes with thinning or patches, a hair evaluation helps identify the cause and guide appropriate treatment.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is hair fall after pregnancy permanent?",
        a: "Usually not. It is typically temporary and settles as hormones return to normal, though it can take several months.",
      },
      {
        q: "Why is my hair falling a lot?",
        a: "It can be due to stress, hormones, nutrition or an underlying condition. A specialist evaluation can identify the cause.",
      },
    ],
    seo: {
      title: "Hair Fall in Women: Causes & Care | AV Clinic",
      description:
        "Learn the causes of hair fall in women — hormones, stress and nutrition — and how it is treated. Hair care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "dandruff-causes-treatment",
    title: "Dandruff: Causes and Treatment",
    category: "Hair Care",
    excerpt:
      "Dandruff is common and manageable. Learn what causes it and how to keep your scalp healthy.",
    image: img.svcDandruff,
    imageAlt: "Dandruff causes and treatment",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-16",
    modified: "2026-08-16",
    readTime: "4 min read",
    tags: ["dandruff", "itchy scalp", "flaking", "dermatology"],
    primaryKeyword: "dandruff treatment",
    secondaryKeywords: ["dandruff causes", "how to treat dandruff", "flaky scalp"],
    relatedService: "dandruff",
    relatedServiceLabel: "Dandruff Treatment",
    relatedArticles: ["scalp-health-hair-growth", "hair-loss-causes", "dry-skin-causes-care"],
    sections: [
      {
        heading: "Why dandruff happens",
        body: [
          "Dandruff is often linked to an overgrowth of a yeast that lives naturally on the scalp, combined with excess oil. This can cause flaking and itching.",
        ],
      },
      {
        heading: "Care that helps",
        body: ["Managing dandruff requires consistency:"],
        list: [
          "Regular washing with an appropriate anti-dandruff shampoo",
          "Gentle scalp care",
          "Avoiding harsh products",
          "Reducing excess oil and product build-up",
        ],
      },
      {
        heading: "When to see a specialist",
        body: [
          "If dandruff is severe, painful, or accompanied by hair loss or patches, a specialist evaluation helps rule out other scalp conditions and guide treatment.",
        ],
      },
      {
        heading: "Stick with it",
        body: [
          "Dandruff can recur, so consistency is key. Using the right products regularly keeps your scalp in balance.",
        ],
      },
    ],
    faqs: [
      {
        q: "How often should I wash my hair with dandruff?",
        a: "This depends on your scalp type, but regular washing with an appropriate shampoo is usually recommended to control oil and flakes.",
      },
      {
        q: "Is dandruff contagious?",
        a: "No. Dandruff is not contagious and cannot be passed to others.",
      },
    ],
    seo: {
      title: "Dandruff: Causes & Treatment | AV Clinic",
      description:
        "Learn the causes of dandruff and how to treat an itchy, flaky scalp. Hair and scalp care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "scalp-health-hair-growth",
    title: "Scalp Health and Hair Growth",
    category: "Hair Care",
    excerpt:
      "A healthy scalp supports healthy hair. Learn how scalp care can improve the condition and growth of your hair.",
    image: img.svcScalpDisorders,
    imageAlt: "Scalp health and hair growth",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-12",
    modified: "2026-08-12",
    readTime: "4 min read",
    tags: ["scalp health", "hair growth", "hair care", "dermatology"],
    primaryKeyword: "scalp health and hair growth",
    secondaryKeywords: ["healthy scalp", "hair growth tips", "scalp care"],
    relatedService: "scalp-disorders",
    relatedServiceLabel: "Scalp Disorder Care",
    relatedArticles: ["dandruff-causes-treatment", "hair-loss-causes", "hair-growth"],
    sections: [
      {
        heading: "Healthy hair starts with the scalp",
        body: [
          "The scalp is the foundation of healthy hair. A balanced, well-cared-for scalp supports stronger growth and reduces excess shedding.",
        ],
      },
      {
        heading: "Scalp-care habits",
        body: ["Support your scalp with these habits:"],
        list: [
          "Gentle, regular cleansing to avoid build-up",
          "Avoiding harsh or abrasive products",
          "Not over-washing or over-heating the scalp",
          "Loose hairstyles that do not pull the roots",
        ],
      },
      {
        heading: "When the scalp needs attention",
        body: [
          "If you have persistent itching, flaking, soreness or a visibly inflamed scalp, a specialist evaluation can identify the cause and guide care.",
        ],
      },
      {
        heading: "Support growth with health",
        body: [
          "Scalp care works best alongside good overall health — a balanced diet, adequate sleep and managing stress all support hair growth.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does a good scalp treatment grow hair?",
        a: "A healthy scalp supports hair growth, but a scalp care routine alone may not reverse significant hair loss. A specialist can advise.",
      },
      {
        q: "How often should I wash my scalp?",
        a: "For most people, a few times a week is fine, depending on scalp type and lifestyle. Balance is key to avoid dryness or excess oil.",
      },
    ],
    seo: {
      title: "Scalp Health & Hair Growth | AV Clinic",
      description:
        "Learn how scalp health supports hair growth and the habits that keep your scalp balanced. Hair care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "male-pattern-hair-loss",
    title: "Male Pattern Hair Loss",
    category: "Hair Care",
    excerpt:
      "Male pattern hair loss is the most common type in men. Learn how it progresses and what can be done early.",
    image: img.svcHairLoss,
    imageAlt: "Male pattern hair loss",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-08",
    modified: "2026-08-08",
    readTime: "5 min read",
    tags: ["male pattern hair loss", "androgenetic alopecia", "receding hairline", "dermatology"],
    primaryKeyword: "male pattern hair loss",
    secondaryKeywords: ["androgenetic alopecia men", "receding hairline", "men hair loss Krishnagiri"],
    relatedService: "hair-loss",
    relatedServiceLabel: "Hair Loss Treatment",
    relatedArticles: ["hair-fall-men", "hair-thinning-causes", "hair-growth"],
    sections: [
      {
        heading: "The most common type of male hair loss",
        body: [
          "Male pattern hair loss, also called androgenetic alopecia, is the most common cause of hair loss in men. It usually starts with a receding hairline or thinning at the crown and is strongly influenced by genetics.",
        ],
      },
      {
        heading: "How it progresses",
        body: [
          "It typically progresses gradually over years. The pattern varies — some men develop a receding hairline, others thinning at the crown, and some both.",
        ],
      },
      {
        heading: "Why early action matters",
        body: [
          "Hereditary hair loss responds better to early treatment. Starting care sooner can slow progression and preserve more hair.",
        ],
      },
      {
        heading: "What a specialist offers",
        body: [
          "A dermatologist can confirm the pattern and discuss realistic treatment options. Managing expectations is important, as complete regrowth is not guaranteed.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can male pattern hair loss be stopped?",
        a: "Early treatment can slow or stabilise it, but it cannot always be fully stopped. A specialist can advise on options.",
      },
      {
        q: "Is male pattern hair loss inherited?",
        a: "Yes. Genetics is the main driver, which is why it tends to run in families.",
      },
    ],
    seo: {
      title: "Male Pattern Hair Loss | AV Clinic",
      description:
        "Learn about male pattern hair loss — how it progresses and why early treatment matters. Hair loss care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "female-pattern-hair-loss",
    title: "Female Pattern Hair Loss",
    category: "Hair Care",
    excerpt:
      "Female pattern hair loss causes gradual thinning, often at the part. Learn what it looks like and how it is managed.",
    image: img.svcHairLoss,
    imageAlt: "Female pattern hair loss",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-04",
    modified: "2026-08-04",
    readTime: "4 min read",
    tags: ["female pattern hair loss", "hair thinning women", "dermatology", "hair care"],
    primaryKeyword: "female pattern hair loss",
    secondaryKeywords: ["women hair thinning", "female hair loss", "hair loss treatment women"],
    relatedService: "hair-loss",
    relatedServiceLabel: "Hair Loss Treatment",
    relatedArticles: ["hair-fall-women", "hair-thinning-causes", "hair-loss-causes"],
    sections: [
      {
        heading: "How it shows up",
        body: [
          "Female pattern hair loss usually causes gradual thinning, often at the crown or widening part, rather than a receding hairline. The hairline is generally preserved.",
        ],
      },
      {
        heading: "What causes it",
        body: [
          "It is largely genetic and can be influenced by hormonal changes. It often becomes more noticeable with age, especially around or after menopause.",
        ],
      },
      {
        heading: "Managing it",
        body: [
          "Because thinning is gradual, early recognition helps. A dermatologist can confirm the pattern and discuss realistic treatment options and expectations.",
        ],
      },
      {
        heading: "The importance of a proper diagnosis",
        body: [
          "Hair loss can have several causes, so a proper evaluation is important. This ensures the right treatment rather than guesswork.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can female pattern hair loss be reversed?",
        a: "It can often be slowed and sometimes improved with early treatment, though regrowth is not guaranteed.",
      },
      {
        q: "Does female pattern hair loss cause complete baldness?",
        a: "Usually not. It tends to cause thinning rather than complete baldness, though the extent varies.",
      },
    ],
    seo: {
      title: "Female Pattern Hair Loss | AV Clinic",
      description:
        "Learn about female pattern hair loss — how it appears and how it is managed. Hair loss care for women at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "hair-thinning-causes",
    title: "Hair Thinning: Causes",
    category: "Hair Care",
    excerpt:
      "Thinning often happens gradually. Learn what causes it and the steps to take early.",
    image: img.svcHairHealth,
    imageAlt: "Hair thinning causes",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-07-31",
    modified: "2026-07-31",
    readTime: "4 min read",
    tags: ["hair thinning", "hair density", "hair loss", "dermatology"],
    primaryKeyword: "hair thinning",
    secondaryKeywords: ["thinning hair", "hair density", "hair loss signs"],
    relatedService: "hair-loss",
    relatedServiceLabel: "Hair Loss Treatment",
    relatedArticles: ["hair-loss-causes", "male-pattern-hair-loss", "female-pattern-hair-loss"],
    sections: [
      {
        heading: "Thinning is gradual",
        body: [
          "Hair thinning often develops slowly, making it easy to miss. A wider parting, a thinner ponytail or more hair in the drain are common early signs.",
        ],
      },
      {
        heading: "Common causes",
        body: ["Thinning hair can be caused by:"],
        list: [
          "Hereditary pattern hair loss",
          "Hormonal changes",
          "Stress and illness",
          "Nutritional deficiencies",
          "Hair-care practices",
        ],
      },
      {
        heading: "Why acting early matters",
        body: [
          "Hair loss conditions such as hereditary thinning respond better to treatment when started early. A specialist evaluation confirms the pattern and advises the most suitable approach.",
        ],
      },
      {
        heading: "Steps to take",
        body: [
          "If you notice thinning, start with a hair evaluation rather than waiting. Early guidance gives the best chance of preserving hair density.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can hair thinning be reversed?",
        a: "Depending on the cause, it can often be slowed or improved, especially with early intervention.",
      },
      {
        q: "Is a wider parting a sign of hair thinning?",
        a: "It can be an early sign of thinning, especially if it is increasing. A specialist evaluation can confirm.",
      },
    ],
    seo: {
      title: "Hair Thinning: Causes & Steps to Take | AV Clinic",
      description:
        "Learn the causes of hair thinning and the steps to take early. Hair loss evaluation at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "hair-loss-treatment",
    title: "Hair Loss Treatment Options",
    category: "Hair Care",
    excerpt:
      "Hair loss can often be slowed or improved. Learn the treatment options and what is realistic.",
    image: img.svcHairGrowth,
    imageAlt: "Hair loss treatment options",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-07-27",
    modified: "2026-07-27",
    readTime: "5 min read",
    tags: ["hair loss treatment", "hair regrowth", "hair care", "dermatology"],
    primaryKeyword: "hair loss treatment",
    secondaryKeywords: ["hair fall treatment Krishnagiri", "hair regrowth", "hair loss medicine"],
    relatedService: "hair-growth-treatments",
    relatedServiceLabel: "Hair Growth Treatment",
    relatedArticles: ["hair-loss-causes", "hair-growth", "when-to-see-dermatologist-hair-loss"],
    sections: [
      {
        heading: "Treatment starts with the cause",
        body: [
          "The right treatment for hair loss depends on its cause. A proper evaluation — rather than a generic product — is the safest starting point.",
        ],
      },
      {
        heading: "Options that may help",
        body: ["Depending on the cause, options may include:"],
        list: [
          "Topical treatments applied to the scalp",
          "Oral medication, where appropriate",
          "Nutritional support for any deficiency",
          "Hair-growth treatments and procedures, in selected cases",
        ],
      },
      {
        heading: "Setting realistic expectations",
        body: [
          "Hair loss treatment takes time, and results vary. Early treatment gives the best chance, but complete regrowth is not guaranteed. A specialist will be honest about what is realistic.",
        ],
      },
      {
        heading: "Consistency is key",
        body: [
          "Hair growth is slow, so consistency and patience matter. Following the plan and attending follow-up helps you get the best result.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long before hair loss treatment shows results?",
        a: "Improvements typically take several months, as hair growth is slow. Consistency is important.",
      },
      {
        q: "Does hair loss treatment work for everyone?",
        a: "Results vary by cause and individual. A specialist can advise what is realistic for you.",
      },
    ],
    seo: {
      title: "Hair Loss Treatment Options | AV Clinic",
      description:
        "Explore hair loss treatment options in Krishnagiri — topical, oral and specialist care. Hair treatment at AV Clinic.",
    },
  },
  {
    slug: "when-to-see-dermatologist-hair-loss",
    title: "When to See a Dermatologist for Hair Loss?",
    category: "Hair Care",
    excerpt:
      "Not every hair fall needs a specialist, but many do. Learn when a hair and scalp evaluation is worthwhile.",
    image: img.svcHairScalpEval,
    imageAlt: "When to see a dermatologist for hair loss",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-07-23",
    modified: "2026-07-23",
    readTime: "5 min read",
    tags: ["hair evaluation", "hair loss specialist", "dermatologist", "scalp check"],
    primaryKeyword: "when to see a dermatologist for hair loss",
    secondaryKeywords: ["hair loss specialist Krishnagiri", "hair evaluation", "hair fall doctor"],
    relatedService: "hair-scalp-evaluation",
    relatedServiceLabel: "Hair & Scalp Evaluation",
    relatedArticles: ["hair-loss-causes", "hair-thinning-causes", "scalp-health-hair-growth"],
    sections: [
      {
        heading: "When to consider a specialist",
        body: [
          "A little daily shedding is normal. But if you notice a continuing increase in hair fall, visible thinning, or patches, a hair and scalp evaluation is worth doing.",
        ],
      },
      {
        heading: "Signs that warrant a visit",
        body: ["See a specialist if you notice:"],
        list: [
          "Heavy shedding that is increasing over weeks",
          "A clearly widening part or receding hairline",
          "Thinning or a bald patch",
          "Scalp itching, flaking or soreness with hair loss",
          "Sudden or unexplained hair loss",
        ],
      },
      {
        heading: "What the evaluation involves",
        body: [
          "A hair and scalp evaluation examines the pattern of hair loss and your scalp, and considers your history and any relevant factors. It helps identify the cause and the right plan.",
        ],
      },
      {
        heading: "Early action helps",
        body: [
          "Many causes of hair loss are treatable, especially when addressed early. A specialist evaluation is a worthwhile, proactive step.",
        ],
      },
    ],
    faqs: [
      {
        q: "Should I see a doctor for hair loss?",
        a: "If hair loss is heavy, persistent or noticeable, a specialist evaluation is worthwhile to find the cause.",
      },
      {
        q: "Can a blood test help with hair loss?",
        a: "In some cases, a blood test checks for deficiencies or underlying conditions that can cause hair loss.",
      },
    ],
    seo: {
      title: "When to See a Dermatologist for Hair Loss | AV Clinic",
      description:
        "Learn when to see a dermatologist for hair loss in Krishnagiri and what a hair evaluation involves. Specialist hair care at AV Clinic.",
    },
  },
];